import React, { useEffect, useState } from 'react';

import { Input } from 'shared/components/ihcl/input';
import { MonthPicker as SplitFieldsMonthPicker } from 'shared/components/ihcl/month_picker_split_fields';

export const dateMatchRE = /([0-9]{2}?)(?:(?: )?(?:\/)(?: )?)([1-2][0-9]{3})/;
export const dateMatchYYYYMM =
  /([1-2][0-9]{3}?)(?:(?: )?(?:-)(?: )?)([0-9]{2})/;

export const getMonthTextFromDate = (isoDateText) =>
  isoDateText
    ? new Date(`${isoDateText}T00:00`).toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      })
    : null;

export const getDateFromMonthText = (monthYearString) => {
  const dateVal = monthYearString?.match(dateMatchYYYYMM);
  if (dateVal && dateVal.length > 0) {
    return new Date(`${dateVal[0]}-01T00:00`);
  }
  return null;
};

export enum Size {
  default = 'default',
  compact = 'compact',
  tiny = 'tiny',
}

type MonthPickerProps = {
  id?: string;
  label?: string;
  max?: string;
  min?: string;
  onChange: Function;
  size?: Size;
  value?: string;
  error?: boolean;
  disabled?: boolean;
  caption?: string;
};
export const MonthPicker: React.FC<MonthPickerProps> = ({
  id = undefined,
  label = undefined,
  max = undefined,
  min = undefined,
  onChange,
  size = Size.default,
  value = undefined,
  error = false,
  disabled = false,
  caption = null,
}) => {
  const [useMonthInput, setUseMonthInput] = useState(true);

  useEffect(() => {
    const testInput = document.createElement('input');
    try {
      testInput.type = 'month';
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e.message);
    }
    if (testInput.type === 'text') {
      setUseMonthInput(false);
    }
  }, []);

  // Native month input for supported browsers (Chrome, Edge, Opera)
  if (useMonthInput) {
    return (
      <Input
        id={id}
        type="month"
        label={label}
        min={min}
        max={max}
        onChange={(e) => {
          const changeValue = e.currentTarget.value;
          onChange(changeValue);
        }}
        size={size}
        value={!disabled ? value : ''}
        error={error}
        disabled={disabled}
        caption={caption}
      />
    );
  }

  // Fallback for browsers that don't support type="month" (Firefox, Safari)
  let yearValue = null;
  let monthValue = null;
  // Parse the YYYY-MM value into year and month for the split fields component
  if (value) {
    const dateMatch = value.match(dateMatchYYYYMM);
    if (dateMatch) {
      const [, year, month] = dateMatch;
      yearValue = year;
      monthValue = month;
    }
  }

  // Parse min/max for year boundaries (only if provided)
  const minYear = min?.match(dateMatchYYYYMM)?.[1];
  const maxYear = max?.match(dateMatchYYYYMM)?.[1];

  return (
    <SplitFieldsMonthPicker
      yearLabel={label ? `${label} Year` : 'Year'}
      monthLabel={label ? `${label} Month` : 'Month'}
      maxYear={maxYear}
      minYear={minYear}
      onChange={({ year, month }) => {
        if (year && month) {
          onChange(`${year}-${month}`);
        } else {
          onChange(null);
        }
      }}
      size={size as 'default' | 'large' | 'compact' | 'mini'}
      yearValue={yearValue}
      monthValue={monthValue}
      error={error ? 'Required' : null}
      disabled={disabled}
      caption={caption}
      instanceId={id || 'month-picker'}
    />
  );
};
